// src/modules/app-views/omnichannel/pages/Init/Conversation.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useRef, useState, useEffect, useCallback } from 'react';
import { theme } from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';
import omnichannelFloatButton from '../actions/floatButton';
import TransferModal from '../modals/TransferModal';
import CloseContextModal from '../modals/CloseContextModal';
import EscalateModal from '../modals/EscalateModal';
import ConversationHeader from './ConversationHeader';
import ConversationBody from './ConversationBody';
import ConversationFooter from './ConversationFooter';
import ConversationOverlay from './ConversationOverlay';
import TranslateProgressOverlay from './TranslateProgressOverlay';
import { useParams, useNavigate } from 'react-router-dom';
import omnichannelService from '../../../service';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { parsePhoneToDisplay } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import { connect as sseConnect, subscribe as sseSubscribe, emitLocal } from './omnichannelSseManager';
import { updateConversationSnapshot } from './ChatMenu';
import whatsappWallpaperDark from 'assets/images/whatsapp-wallpaper-dark.png';
import whatsappWallpaperLight from 'assets/images/whatsapp-wallpaper-light.png';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const BLOCK_VIEW_CONVERSATION_NOT_ASSIGN_TO = true;
const MASKED_PHONE = true;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const Conversation = ({ isFullscreen, onToggleFullscreen, basePath, conversationId: conversationIdProp, onNavigateAway, embedded, onEmbeddedSourceChange, embeddedDisplayName, embeddedCrmId }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // Zustand — subscription identifies the SSE channel for this tenant
  const storedSubscription = useZustand('auth', 'subscription');
  const storedUser = useZustand('auth', 'user');
  const storedMe = useZustand('auth', 'me');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const formRef = useRef();
  const chatBodyRef = useRef();
  const inputRef = useRef();
  // Buffer for SSE status updates that arrive before the message is in React state.
  // Race condition: Meta fires 'sent'/'delivered' webhooks within ~1s of send, but
  // the sendWhatsappMessage API response may not have returned yet, so the message
  // has no externalMessageId to match. On API response, flush the buffer.
  const pendingStatusUpdatesRef = useRef({});
  // Tracks whether translateLang was just changed by user (to show overlay only on manual change)
  const translateLangChangedRef = useRef(false);
  // Tracks the last conversation _id seen by the auto-translate effect (to detect conversation open)
  const prevTranslateConvIdRef = useRef(null);
  // Per-conversation outbound translate lang: Map<conversationId, lang>
  const outboundLangsRef = useRef(new Map());
  // Suppress SSE reaction events right after agent sends a reaction (prevents optimistic → SSE flicker)
  // Set<messageIndex> — entries auto-expire after a short timeout
  const pendingReactionIndicesRef = useRef(new Set());
  // Auto-scroll on content resize (media loading changes bubble height) — active for 5s after conversation open
  const autoScrollUntilRef = useRef(0);
  // Cancel flag for batch translation — set to true by handleCancelTranslation, checked in the loop
  const cancelTranslationRef = useRef(false);
  // Refs for SSE handlers — avoids re-creating SSE connection on every conversation switch
  const conversationIdRef = useRef(null);
  const conversationRef = useRef(null);
  // In embedded CRM mode, holds the context._id that belongs to this CRM opportunity.
  // SSE messages with a different contextId are filtered out.
  const crmContextIdRef = useRef(null);
  // Tracks whether the user manually picked a source — prevents the sync effect from
  // resetting selectedSourceId back to the conversation's sourceId while the new
  // conversation is still loading (stale conversation state would cause a revert to Meta).
  const userSelectedSourceRef = useRef(false);
  const navigate = useNavigate();
  const params = useParams();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const { _id: _idFromParams } = params;
  // Allow embedding outside of omnichannel route: override _id via prop
  const _id = conversationIdProp || _idFromParams;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [sources, setSources] = useState([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  // ANTD v6 fix: controlled open state for Select dropdown
  const [sourceSelectOpen, setSourceSelectOpen] = useState(false);
  const [floatOpen, setFloatOpen] = useState(false);
  const [attendLoading, setAttendLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCloseContextModal, setShowCloseContextModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  // Translation cache: Map<messageIndex, { text, lang, provider }>
  const [translations, setTranslations] = useState({});
  const [translatingIndex, setTranslatingIndex] = useState(null);
  // Auto-translate: localStorage is primary (sync, survives refresh); module prefs as secondary sync
  const [translateLang, setTranslateLang] = useState(
    () => localStorage.getItem('omnichannel_translate_lang') || 'none'
  );
  // Batch translation progress: { total, done, active, fromLang, toLang }
  const [translateProgress, setTranslateProgress] = useState(null);
  // Indices of messages being auto-translated inline (no overlay — used for new incoming messages)
  const [autoTranslatingIndices, setAutoTranslatingIndices] = useState(new Set());
  // Outbound language for this conversation: 'none' = send as typed, otherwise translate before send
  const [outboundLang, setOutboundLang] = useState('none');
  // For outbound messages translated before sending: { [idx]: originalTypedText }
  // Bubble shows original by default; translate icon reveals what was delivered
  const [outboundOriginals, setOutboundOriginals] = useState({});
  // Delivery lang used per outbound message: { [idx]: 'fr' } — drives the flag shown next to the translate icon
  const [outboundDeliveryLangs, setOutboundDeliveryLangs] = useState({});
  // Translation errors per outbound message: { [idx]: 'timeout' | 'generic' } — flag + warning icon on bubble
  const [outboundTranslateErrors, setOutboundTranslateErrors] = useState({});
  // Avatar refresh: true while the force-refresh request is in flight
  const [refreshingAvatar, setRefreshingAvatar] = useState(false);


  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Load sources (Meta + Baileys) on mount and when window regains focus
  // (catches new sources added via parameters modal without needing page refresh)
  // Also called after a failed send to refresh connectionStatus from the backend.
  const refreshSources = useCallback(async () => {
    try {
      const [data] = await omnichannelService.getSources();
      setSources(Array.isArray(data) ? data : []);
      setSourcesLoaded(true);
    } catch {
      // Sources unavailable — keep current state
    }
  }, []);

  useEffect(() => {
    let active = true;

    const fetchSources = async () => {
      try {
        const [data] = await omnichannelService.getSources();
        if (active) {
          // Normalize: backend may return null when there are no sources
          setSources(Array.isArray(data) ? data : []);
          setSourcesLoaded(true);
        }
      } catch {
        // Sources unavailable — keep sourcesLoaded false to avoid false positives
      }
    };

    fetchSources();

    const handleFocus = () => fetchSources();
    // Also refresh when a Baileys instance reconnects via the parameters modal
    const handleSourcesRefresh = () => fetchSources();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('omnichannel:sources-refresh', handleSourcesRefresh);

    return () => {
      active = false;
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('omnichannel:sources-refresh', handleSourcesRefresh);
    };
  }, []);

  useEffect(() => {
    if (!_id) return;

    let active = true;

    // Clear stale data immediately so the translate effect sees empty messages
    // and returns early (prevents sending old conversation indices to the new conversation API)
    setMessages([]);
    setTranslations({});
    // New conversation loading — allow the sync effect to update the selected source
    userSelectedSourceRef.current = false;

    const fetchConversation = async () => {
      setLoadingConversation(true);
      try {
        const [data] = await omnichannelService.getConversation(_id);
        if (!active) return;

        if (!data) {
          // Conversation not found — redirect to init page
          if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); }
          return;
        }

        setConversation(data);
        setMessages(data.messages || []);

        // Resolve which context belongs to this CRM opportunity (if embedded in CRM mode).
        // Used by the SSE handler to filter out messages from other attendance cycles.
        if (embeddedCrmId && data.contexts?.length) {
          const crmCtx = data.contexts.find(c =>
            c.linkedEntityType === 'crm' && String(c.linkedEntityId) === String(embeddedCrmId)
          );
          crmContextIdRef.current = crmCtx?._id || null;
        } else {
          crmContextIdRef.current = null;
        }

        // Sync ChatMenu cache with fresh contactSnapshot from getById.
        // Updates the module-level cache directly — survives remounts.
        if (data.contactSnapshot) {
          const { members: _m, ...snapshotForMenu } = data.contactSnapshot;
          updateConversationSnapshot(data._id, snapshotForMenu);
        }
        // Rebuild outbound originals and delivery langs from DB
        const originals = {};
        const deliveryLangs = {};
        (data.messages || []).forEach((msg, idx) => {
          if (msg.direction === 'outbound' && msg.content?.originalText) {
            originals[idx] = msg.content.originalText;
            if (msg.content.deliveryLang) deliveryLangs[idx] = msg.content.deliveryLang;
          }
        });
        setOutboundOriginals(originals);
        setOutboundDeliveryLangs(deliveryLangs);
        // Restore delivery lang saved in this conversation (primary source — saved in DB)
        const savedDeliveryLang = data.deliveryLang || 'none';
        setOutboundLang(savedDeliveryLang);
        outboundLangsRef.current.set(_id, savedDeliveryLang);
        // Pre-select the source linked to this conversation
        if (data.sourceId) {
          setSelectedSourceId(data.sourceId);
        }
        // Enable auto-scroll for 5s — ResizeObserver will scroll on each content height change
        // (catches images/videos loading and expanding bubble height)
        autoScrollUntilRef.current = Date.now() + 5000;
        setTimeout(scrollToBottom, 50);
        setTimeout(scrollToBottom, 300);
        setTimeout(() => inputRef.current?.focus(), 100);
        // Mark all inbound messages as read (sends blue checkmarks via Meta API).
        // Only mark as read if conversation is assigned to this agent (not in waiting queue).
        if (data.unreadCount > 0 && data.assignedTo && data.assignedTo === storedUser?._id) {
          omnichannelService.markAsRead(_id).catch(() => {});
        }
      } catch {
        // Conversation not found or backend offline — redirect to init page
        if (active) {
          if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); }
        }
      } finally {
        if (active) setLoadingConversation(false);
      }
    };

    fetchConversation();

    return () => {
      active = false;
    };
  }, [_id]);

  // Clear outbound originals/langs when switching conversations (fetchConversation rebuilds from DB)
  useEffect(() => {
    if (!_id) return;
    setOutboundOriginals({});
    setOutboundDeliveryLangs({});
  }, [_id]);

  // Focus message input after fullscreen toggle (wait for transition)
  useEffect(() => {
    if (isFullscreen === undefined) return;
    setTimeout(() => inputRef.current?.focus(), 350);
  }, [isFullscreen]);

  // Auto-scroll when chat content resizes (media loading expands bubbles)
  // Active for 5s after conversation open, then stops to avoid fighting user scroll
  useEffect(() => {
    const scrollContainer = chatBodyRef.current?.container?.firstChild || chatBodyRef.current?.view;
    if (!scrollContainer) return;

    const observer = new ResizeObserver(() => {
      if (Date.now() < autoScrollUntilRef.current) {
        scrollToBottom();
      }
    });

    // Observe the inner content wrapper (its height changes as media loads)
    const contentEl = scrollContainer.querySelector('div');
    if (contentEl) observer.observe(contentEl);

    return () => observer.disconnect();
  }, [_id, messages.length]);

  // Sync selected source when conversation changes — always pick the source that matches
  // the current conversation's sourceId/sourceType (not the previous conversation's selection).
  // Guards:
  //   1. userSelectedSourceRef: user manually changed the source → skip until new conversation loads
  //   2. conversation._id !== _id: conversation state is stale (still loading) → skip to prevent
  //      reverting to the previous source while the new conversation data is in flight
  useEffect(() => {
    if (!conversation || sources.length === 0) return;
    if (userSelectedSourceRef.current) return;
    if (String(conversation._id) !== String(_id)) return;

    // Try to match by sourceId from conversation
    if (conversation.sourceId && sources.some(s => s._id === conversation.sourceId)) {
      setSelectedSourceId(conversation.sourceId);
      return;
    }

    // Fallback: try user's last-used source stored in localStorage (set by empty state selector)
    const storedSourceId = localStorage.getItem('omnichannel_selected_source');
    if (storedSourceId && sources.some(s => s._id === storedSourceId)) {
      setSelectedSourceId(storedSourceId);
      return;
    }

    // Last resort: select the first source of the matching sourceType, then first available
    const fallback = sources.find(s => s.sourceType === (conversation.sourceType || 'meta'));
    if (fallback) {
      setSelectedSourceId(fallback._id);
    } else if (sources.length > 0) {
      setSelectedSourceId(sources[0]._id);
    }
  }, [_id, conversation?._id, sources]);

  // Auto-assign: in embedded (CRM drawer) mode, assign the conversation to the current
  // agent as soon as it loads — no need to click "Attend" here.
  useEffect(() => {
    if (!embedded || !conversation?._id || conversation.assignedTo || !storedUser?._id) return;
    omnichannelService
      .assignConversation(conversation._id, storedUser._id)
      .then(() => {
        setConversation(prev => (prev ? { ...prev, assignedTo: storedUser._id } : prev));
      })
      .catch(() => {});
  }, [embedded, conversation?._id, conversation?.assignedTo, storedUser?._id]);

  // Auto-fetch avatar: in embedded mode, silently request WhatsApp profile picture
  // when the conversation finishes loading and no avatar is cached yet.
  // conversation?._id in deps ensures re-run when the conversation object arrives
  // (conversation transitions null → object while _id and !!avatar both stay unchanged).
  useEffect(() => {
    if (!embedded || !_id || !conversation || conversation.contactSnapshot?.avatar) return;
    omnichannelService.refreshAvatar(_id).catch(() => {});
  }, [embedded, _id, conversation?._id, !!conversation?.contactSnapshot?.avatar]);

  // Keep refs in sync so SSE handlers always read the current conversation
  conversationIdRef.current = _id;
  conversationRef.current = conversation;

  // SSE effect — subscribe to the shared SSE manager (one connection for the entire module).
  useEffect(() => {
    const subscriptionId = storedSubscription?._id;
    if (!subscriptionId) return;

    sseConnect(subscriptionId);

    const unsubs = [
      sseSubscribe('omnichannel:message-received', ({ conversationId, message }) => {
        const currentId = conversationIdRef.current;
        if (conversationId !== currentId || !message) return;
        if (message.direction !== 'inbound') return;
        setMessages(prev => {
          // Dedup: backend emits twice — once in the webhook handler and once via
          // MongoDB change stream (~50-200ms later). Skip if already in state.
          const isDup = prev.some(m =>
            (message._id && m._id === message._id) ||
            (message.externalMessageId && m.externalMessageId === message.externalMessageId)
          );
          if (isDup) return prev;
          return [...prev, message];
        });
        setTimeout(scrollToBottom, 100);

        // If message has media but no URL yet (uploading to storage), poll for the update
        if (message.content?.mediaType && !message.content?.mediaUrl && message.externalMessageId) {
          pollForMediaUrl(conversationId, message.externalMessageId, setMessages, scrollToBottom);
        }
        const conv = conversationRef.current;
        if (conv?.assignedTo && conv.assignedTo === storedUser?._id) {
          omnichannelService.markAsRead(currentId).catch(() => {});
        }
      }),
      sseSubscribe('omnichannel:message-status-updated', ({ externalMessageId: msgId, status }) => {
        if (!msgId || !status) return;
        setMessages(prev => {
          const hasMatch = prev.some(m => m.externalMessageId === msgId);
          if (hasMatch) return prev.map(m => (m.externalMessageId === msgId ? { ...m, status } : m));
          pendingStatusUpdatesRef.current[msgId] = status;
          return prev;
        });
      }),
      // Baileys (unofficial) fires message-sent when a message is confirmed by the server.
      // Only update status — never append. Appending causes duplicates because the optimistic
      // message is already in the list but doesn't have externalMessageId yet, so hasMatch
      // fails and the SSE message gets added as a second copy before the API response arrives.
      // If the message isn't tracked yet (API response still in flight), buffer the status
      // via pendingStatusUpdatesRef so it's flushed when the optimistic message is replaced.
      sseSubscribe('omnichannel:message-sent', ({ conversationId, message }) => {
        if (conversationId !== conversationIdRef.current || !message?.externalMessageId) return;
        setMessages(prev => {
          const hasMatch = prev.some(m => m.externalMessageId === message.externalMessageId);
          if (!hasMatch) {
            if (message.status) pendingStatusUpdatesRef.current[message.externalMessageId] = message.status;
            return prev;
          }
          return prev.map(m =>
            m.externalMessageId === message.externalMessageId
              ? { ...m, status: message.status || m.status }
              : m
          );
        });
      }),
      sseSubscribe('omnichannel:contact-snapshot-updated', ({ conversationId, contactSnapshot }) => {
        if (conversationId !== conversationIdRef.current || !contactSnapshot) return;
        setConversation(prev => prev ? { ...prev, contactSnapshot: { ...prev.contactSnapshot, ...contactSnapshot } } : prev);
      }),
      sseSubscribe('omnichannel:message-reaction', ({ conversationId, messageIndex, reactions }) => {
        if (conversationId !== conversationIdRef.current || typeof messageIndex !== 'number') return;
        if (pendingReactionIndicesRef.current.has(messageIndex)) return;
        setMessages(prev => {
          const updated = prev.map((m, i) => (i === messageIndex ? { ...m, reactions } : m));
          if (messageIndex === prev.length - 1) setTimeout(scrollToBottom, 100);
          return updated;
        });
      }),
      sseSubscribe('omnichannel:message-media-updated', ({ conversationId, messageIndex, externalMessageId, mediaUrl, mimeType, fileSize }) => {
        if (conversationId !== conversationIdRef.current || !mediaUrl) return;
        setMessages(prev => {
          const updated = prev.map((m, i) => {
            // Match by externalMessageId (robust) or fallback to messageIndex
            const isMatch = externalMessageId
              ? m.externalMessageId === externalMessageId
              : i === messageIndex;
            if (!isMatch) return m;
            const updatedContent = { ...m.content, mediaUrl };
            if (mimeType) updatedContent.mimeType = mimeType;
            if (fileSize) updatedContent.fileSize = fileSize;
            return { ...m, content: updatedContent };
          });
          setTimeout(scrollToBottom, 150);
          return updated;
        });
      }),
      sseSubscribe('omnichannel:source-status-changed', ({ sourceId, connectionStatus }) => {
        if (!sourceId || !connectionStatus) return;
        if (connectionStatus === 'removed') {
          // Source was deleted — remove from local list so isChannelRemoved activates
          setSources(prev => prev.filter(s => s._id !== sourceId));
        } else {
          setSources(prev => prev.map(s => s._id === sourceId ? { ...s, connectionStatus } : s));
        }
      }),
    ];

    return () => unsubs.forEach(fn => fn());
  }, [storedSubscription?._id]);

  // Auto-translate: when translateLang changes, conversation opens, or new messages arrive.
  // Strategy:
  //   - User changed the selector (translateLangChangedRef = true)  → sequential + overlay with %
  //   - Conversation just opened (_id changed) with lang already set  → same: sequential + overlay
  //   - New message arrived (neither of the above)                   → translate inline, no overlay
  useEffect(() => {
    if (!translateLang || translateLang === 'none' || !_id || !messages.length) return;

    // Skip if conversation data hasn't been fetched for the current _id yet.
    // When _id changes, this effect re-runs before setMessages([]) is applied,
    // so messages still has stale data from the previous conversation.
    // Without this check, prevTranslateConvIdRef would be set prematurely,
    // causing the overlay to not show when the correct messages arrive.
    if (!conversation || String(conversation._id) !== String(_id)) return;

    // Only translate if this conversation is assigned to the current agent.
    // Unassigned conversations (waiting queue) skip translation even if a lang is selected.
    if (!conversation.assignedTo || conversation.assignedTo !== storedUser?._id) return;

    // Find inbound messages with text that haven't been translated yet
    const toTranslate = [];
    messages.forEach((msg, idx) => {
      if (msg.direction === 'inbound' && msg.content?.text && !translations[idx]) {
        toTranslate.push(idx);
      }
    });

    if (toTranslate.length === 0) return;

    // Show overlay when user changed the selector, opening a new conversation,
    // OR returning to a conversation that still has untranslated messages (e.g. after visiting a queue conversation).
    // When translations were cleared (navigated away and back), toTranslate contains all messages again.
    const conversationJustOpened = prevTranslateConvIdRef.current !== _id;
    const returningWithPending = !conversationJustOpened && Object.keys(translations).length === 0 && toTranslate.length > 0;
    const showOverlay = translateLangChangedRef.current || conversationJustOpened || returningWithPending;
    translateLangChangedRef.current = false;
    prevTranslateConvIdRef.current = _id;

    let cancelled = false;
    cancelTranslationRef.current = false;

    if (showOverlay) {
      // Sequential requests with live progress overlay so user sees the % completing
      setTranslateProgress({
        total: toTranslate.length,
        done: 0,
        active: true,
        toLang: translateLang,
      });
      let doneCount = 0;
      const translateSequential = async () => {
        for (const idx of toTranslate) {
          if (cancelled || cancelTranslationRef.current) break;

          // Check message-level cache (translations[] stored in DB) before hitting the API
          const localHit = messages[idx]?.translations?.find(t => t.lang === translateLang);
          if (localHit) {
            if (!cancelled && !cancelTranslationRef.current) {
              setTranslations(prev => ({
                ...prev,
                [idx]: { text: localHit.text, lang: localHit.lang, provider: localHit.provider, cached: true },
              }));
            }
          } else {
            try {
              const [data] = await omnichannelService.translateMessage(_id, {
                messageIndex: idx,
                targetLang: translateLang,
              });
              if (!cancelled && !cancelTranslationRef.current && data?.text && isValidTranslation(data.text)) {
                setTranslations(prev => ({
                  ...prev,
                  [idx]: { text: data.text, lang: data.lang, provider: data.provider, cached: data.cached },
                }));
              }
            } catch {
              // Skip failed translations silently
            }
          }

          doneCount++;
          if (!cancelled && !cancelTranslationRef.current) {
            setTranslateProgress(prev => (prev ? { ...prev, done: doneCount } : null));
          }
        }
        if (!cancelled && !cancelTranslationRef.current) setTranslateProgress(null);
      };
      translateSequential();
    } else {
      // New message arrived with auto-translate active → translate inline, no overlay
      setAutoTranslatingIndices(prev => {
        const next = new Set(prev);
        toTranslate.forEach(idx => next.add(idx));
        return next;
      });
      const translateInline = async () => {
        for (const idx of toTranslate) {
          if (cancelled) break;

          // Check message-level cache before hitting the API
          const localHit = messages[idx]?.translations?.find(t => t.lang === translateLang);
          if (localHit) {
            if (!cancelled) {
              setTranslations(prev => ({
                ...prev,
                [idx]: { text: localHit.text, lang: localHit.lang, provider: localHit.provider, cached: true },
              }));
            }
          } else {
            try {
              const [data] = await omnichannelService.translateMessage(_id, {
                messageIndex: idx,
                targetLang: translateLang,
              });
              if (!cancelled && data?.text && isValidTranslation(data.text)) {
                setTranslations(prev => ({
                  ...prev,
                  [idx]: { text: data.text, lang: data.lang, provider: data.provider, cached: data.cached },
                }));
              }
            } catch {
              // Skip failed translations silently
            }
          }

          if (!cancelled) {
            setAutoTranslatingIndices(prev => {
              const next = new Set(prev);
              next.delete(idx);
              return next;
            });
          }
        }
      };
      translateInline();
    }

    return () => {
      cancelled = true;
      // Clean up both overlay and inline spinners on conversation change or unmount
      setTranslateProgress(null);
      setAutoTranslatingIndices(new Set());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translateLang, _id, messages.length]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const scrollToBottom = () => {
    chatBodyRef.current?.scrollToBottom();
  };

  const handleSend = async values => {
    if (!values.newMsg || !_id || sending) return;

    const text = values.newMsg.trim();
    if (!text) return;

    const agentName = storedMe?.name || storedMe?.fullName || storedUser?.name || storedUser?.displayName || 'Agente';

    // ── STEP 1: IMMEDIATE ────────────────────────────────────────────────────
    // Show bubble with typed text, clear input and restore focus right away.
    // Translation and network send happen in the background after this point.
    const optimisticIndex = messages.length;
    const optimisticMsg = {
      direction: 'outbound',
      channel: conversation?.channel || 'whatsapp',
      sender: { type: 'agent', name: agentName },
      // Bubble always shows what the agent typed (original language)
      content: { text },
      status: 'queued',
      timestamp: new Date().toISOString(),
      _optimistic: true,
    };

    setMessages(prev => [...prev, optimisticMsg]);
    formRef.current?.setFieldsValue({ newMsg: '' });
    inputRef.current?.focus();
    setTimeout(scrollToBottom, 50);

    // ── STEP 2: BACKGROUND ───────────────────────────────────────────────────
    // Translate if outbound lang is set, then send to recipient.
    setSending(true);
    try {
      let translatedText = text;

      // Translate before delivering (fire after UI is already updated)
      if (outboundLang && outboundLang !== 'none') {
        try {
          const [translated] = await omnichannelService.translateText(_id, {
            text,
            targetLang: outboundLang,
          });
          if (
            translated?.text &&
            isValidTranslation(translated.text)
          ) {
            translatedText = translated.text;
            // Immediately update state so the translate icon and flag appear on the optimistic bubble
            setOutboundOriginals(prev => ({ ...prev, [optimisticIndex]: text }));
            setOutboundDeliveryLangs(prev => ({ ...prev, [optimisticIndex]: outboundLang }));
          } else {
            // Translation returned invalid/error response — show flag with warning (in-memory only).
            // If fallback provider (mymemory) responded, it likely means Azure timed out (network/firewall).
            const isFallbackError = translated?.provider === 'mymemory' || /INVALID|LANGPAIR/.test(translated?.text || '');
            setOutboundDeliveryLangs(prev => ({ ...prev, [optimisticIndex]: outboundLang }));
            setOutboundTranslateErrors(prev => ({
              ...prev,
              [optimisticIndex]: isFallbackError ? 'timeout' : 'generic',
            }));
          }
        } catch (err) {
          // Translation failed (network/timeout) — send original, show flag with warning (in-memory only)
          const errorType = /timeout|ETIMEDOUT|ECONNREFUSED|ENOTFOUND/i.test(err?.message)
            ? 'timeout'
            : 'generic';
          setOutboundDeliveryLangs(prev => ({ ...prev, [optimisticIndex]: outboundLang }));
          setOutboundTranslateErrors(prev => ({ ...prev, [optimisticIndex]: errorType }));
        }
      }

      // Apply prefix after translation — only message content is translated, not the attendant header
      const textToSend = `*${agentName}:*\n${translatedText}`;

      // Build source override if user selected a different source
      const sourceOptions = {};
      if (selectedSourceId) {
        const selectedSource = sources.find(s => s._id === selectedSourceId);
        if (selectedSource) {
          sourceOptions.sourceId = selectedSource._id;
          sourceOptions.sourceType = selectedSource.sourceType;
        }
      }

      // Pass originalText + deliveryLang when translation changed the text — persisted in content by the backend
      const didTranslate = translatedText !== text;
      const originalTextForSave = didTranslate ? text : undefined;
      const deliveryLangForSave = didTranslate ? outboundLang : undefined;
      const [savedMsg] = await omnichannelService.sendWhatsappMessage(
        _id,
        textToSend,
        sourceOptions,
        originalTextForSave,
        deliveryLangForSave
      );

      // Replace optimistic with server response (has externalMessageId for status tracking).
      // If translation happened, server stores textToSend (translated) as content.text
      // and originalText as content.originalText — bubble always shows the original via outboundOriginals.
      setMessages(prev => {
        const idx = prev.findLastIndex(m => m._optimistic);
        if (idx === -1) return prev;
        const updated = [...prev];
        const baseMsg = savedMsg
          ? { ...savedMsg, _optimistic: false }
          : { ...prev[idx], status: 'sent', _optimistic: false };
        // Flush any buffered SSE status updates that arrived before this response
        // (never delete inside updater — React Strict Mode runs it twice in dev)
        const bufferedStatus = baseMsg.externalMessageId
          ? pendingStatusUpdatesRef.current[baseMsg.externalMessageId]
          : null;
        updated[idx] = bufferedStatus ? { ...baseMsg, status: bufferedStatus } : baseMsg;
        return updated;
      });
      setTimeout(scrollToBottom, 50);

      // If the message was rejected by the provider (e.g. Baileys disconnected),
      // refresh sources so isChannelDisconnected updates and blocks further sends.
      if (savedMsg?.status === 'failed') {
        refreshSources();
      }
    } catch {
      // Mark optimistic message as failed and refresh sources to detect disconnected channels
      setMessages(prev =>
        prev.map(m => (m._optimistic ? { ...m, status: 'failed', _optimistic: false } : m))
      );
      refreshSources();
    } finally {
      setSending(false);
    }
  };

  // ANTD v6 fix: controlled open state for source selector
  const handleSourceSelect = useCallback(() => {
    setSourceSelectOpen(false);
  }, []);

  const handleSourceOpenChange = useCallback(visible => {
    setSourceSelectOpen(visible);
  }, []);

  const handleSourceChange = useCallback(sourceId => {
    // Mark as user-driven selection — prevents the sync effect from reverting this
    // back to the previous conversation's sourceId while the new conversation loads
    userSelectedSourceRef.current = true;
    // Optimistic: update local selection immediately for instant UI feedback
    setSelectedSourceId(sourceId);
    // Persist so the empty state selector stays in sync
    localStorage.setItem('omnichannel_selected_source', sourceId);
    // In embedded (drawer) mode: reload the conversation scoped to the new source
    // so that body (messages) and footer (window state) reflect that source
    if (embedded && onEmbeddedSourceChange) {
      onEmbeddedSourceChange(sourceId);
    }
  }, [embedded, onEmbeddedSourceChange]);

  const handleEmptyClick = e => {
    e.preventDefault();
  };

  const handleFloatAction = useCallback(fn => {
    setFloatOpen(false);
    fn?.();
  }, []);

  // Attend handler with race-condition guard:
  //   1. Fetch fresh conversation from backend to detect simultaneous claims
  //   2. If already assigned, show warning notification with agent name and abort
  //   3. If still free, assign and propagate immediately via emitLocal so ChatMenu updates
  //      without waiting for backend SSE (other sessions rely on backend SSE as usual)
  const handleAttend = useCallback(async () => {
    if (!_id || !storedUser?._id) return;
    setAttendLoading(true);
    try {
      const [freshConv] = await omnichannelService.getConversation(_id);
      if (freshConv?.assignedTo) {
        // Already claimed — surface agent name if backend embedded it in the response
        const agentName =
          freshConv.assignedToAgent?.name ||
          freshConv.agentName ||
          null;
        appService.notification({
          flag: 'w',
          title: translateX('conversation_already_attended'),
          description: agentName
            ? `${translateX('attended_by')}: ${agentName}`
            : translateX('conversation_already_attended_by_another'),
        });
        return;
      }
      // Conversation still unassigned — claim it
      await omnichannelService.assignConversation(_id, storedUser._id);
      setConversation(prev => (prev ? { ...prev, assignedTo: storedUser._id } : prev));
      // Immediately propagate to ChatMenu (same tab) without waiting for backend SSE
      emitLocal('omnichannel:conversation-assigned', { conversationId: _id, assignedTo: storedUser._id });
      appService.message({ flag: 's', text: 'assign_conversation_success' });
    } catch {
      appService.message({ flag: 'e', text: 'error_operation' });
    } finally {
      setAttendLoading(false);
    }
  }, [_id, storedUser?._id]);

  const handleTransferSuccess = useCallback(() => {
    // After transfer, redirect back to omnichannel list
    if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); }
  }, [navigate, onNavigateAway]);

  const handleCloseContextSuccess = useCallback(() => {
    // After closing context, redirect back to omnichannel list
    if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); }
  }, [navigate, onNavigateAway]);

  const handleEscalateSuccess = useCallback(() => {
    // After escalation, redirect back to omnichannel list
    if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); }
  }, [navigate, onNavigateAway]);

  const handleTranslateMessage = useCallback(
    async messageIndex => {
      const msg = messages[messageIndex];

      // Outbound messages with pre-send translation: toggle between original typed and delivered text
      if (msg?.direction === 'outbound' && outboundOriginals[messageIndex]) {
        setTranslations(prev => {
          const next = { ...prev };
          if (next[messageIndex]) {
            // Currently showing delivered text → flip back to original
            delete next[messageIndex];
          } else {
            // Currently showing original → flip to show what was delivered
            next[messageIndex] = {
              text: msg.content?.text,
              lang: outboundLang || 'delivered',
              outbound: true,
            };
          }
          return next;
        });
        return;
      }

      // If already translated, toggle off (show original)
      if (translations[messageIndex]) {
        setTranslations(prev => {
          const next = { ...prev };
          delete next[messageIndex];
          return next;
        });
        return;
      }

      setTranslatingIndex(messageIndex);
      try {
        // Use the auto-translate language if set, otherwise fallback to app language
        const storedLang = useZustand('theme', 'language', false);
        const userLang =
          translateLang && translateLang !== 'none'
            ? translateLang
            : (storedLang || navigator.language || 'pt').split(/[-_]/)[0];
        const [data] = await omnichannelService.translateMessage(_id, {
          messageIndex,
          targetLang: userLang,
        });
        if (data?.text) {
          setTranslations(prev => ({
            ...prev,
            [messageIndex]: {
              text: data.text,
              lang: data.lang,
              provider: data.provider,
              cached: data.cached,
            },
          }));
        }
      } catch {
        appService.message({ flag: 'e', text: 'translation_failed' });
      } finally {
        setTranslatingIndex(null);
      }
    },
    [_id, translations, messages, outboundOriginals, outboundLang]
  );

  const handleOutboundLangChange = useCallback(
    lang => {
      setOutboundLang(lang);
      // Persist in-session Map (fast lookup) + conversation document in DB (cross-device, primary)
      outboundLangsRef.current.set(_id, lang);
      omnichannelService.updateConversation(_id, { deliveryLang: lang }).catch(() => {});
    },
    [_id]
  );

  const handleTranslateLangChange = useCallback(lang => {
    setTranslateLang(lang);
    // Clear all cached translations when language changes
    setTranslations({});
    // Mark that the user explicitly changed the selector — triggers overlay on next effect run
    translateLangChangedRef.current = true;
    // Persist immediately via localStorage (reliable across refreshes)
    localStorage.setItem('omnichannel_translate_lang', lang);
    // Also sync to module parameters for cross-device support (best-effort)
    appService
      .updateModulePreferences({
        moduleId: 'omnichannel',
        category: 'translation',
        data: { preferredLang: lang },
        private: true,
      })
      .catch(() => {});
  }, []);

  const handleCancelTranslation = useCallback(() => {
    cancelTranslationRef.current = true;
    setTranslateProgress(null);
    // Reset language to 'none' and clear translations
    setTranslateLang('none');
    setTranslations({});
    localStorage.setItem('omnichannel_translate_lang', 'none');
  }, []);

  const handleReaction = useCallback(
    async (messageIndex, emoji) => {
      if (!_id) return;

      // Suppress incoming SSE reaction events for this index (prevents optimistic → SSE flicker)
      pendingReactionIndicesRef.current.add(messageIndex);
      setTimeout(() => pendingReactionIndicesRef.current.delete(messageIndex), 3000);

      // Optimistic update — show reaction immediately without waiting for SSE
      setMessages(prev =>
        prev.map((m, i) => {
          if (i !== messageIndex) return m;
          // Remove previous agent reaction
          const others = (m.reactions || []).filter(r => r.senderType !== 'agent');
          const reactions = emoji
            ? [...others, { emoji, senderType: 'agent', senderName: 'Me', timestamp: new Date().toISOString() }]
            : others;
          return { ...m, reactions };
        })
      );

      try {
        await omnichannelService.sendReaction(_id, { messageIndex, emoji });
        // SSE suppressed for 3s — optimistic state is already correct
      } catch {
        // Revert optimistic update on failure
        pendingReactionIndicesRef.current.delete(messageIndex);
        setMessages(prev =>
          prev.map((m, i) => {
            if (i !== messageIndex) return m;
            const reverted = (m.reactions || []).filter(r => r.senderType !== 'agent');
            return { ...m, reactions: reverted };
          })
        );
        appService.message({ flag: 'e', text: 'error_operation' });
      }
    },
    [_id]
  );

  const handleRefreshAvatar = useCallback(async () => {
    if (refreshingAvatar || !_id) return;
    setRefreshingAvatar(true);
    try {
      await omnichannelService.refreshAvatar(_id);
      // Avatar update arrives via SSE 'omnichannel:contact-snapshot-updated' — no manual state update needed
    } catch {
      // Silently ignore — avatar stays as-is
    } finally {
      setRefreshingAvatar(false);
    }
  }, [_id, refreshingAvatar]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const rawName =
    conversation?.displayName ||
    conversation?.contactSnapshot?.name ||
    conversation?.contactSnapshot?.phone ||
    '—';
  // When name is a phone number (Meta API contacts without pushName), format and mask it
  const resolvedName = /^\d+$/.test(rawName) ? formatNumericName(rawName) : rawName;
  // In embedded (CRM drawer) mode: prefer the CRM entity name passed by the parent
  // so the header always shows the opportunity contact name rather than the raw WhatsApp name
  const displayName = (embedded && embeddedDisplayName) ? embeddedDisplayName : resolvedName;

  const channelLabel = getChannelLabel(conversation?.channel);

  // Conversation is in queue (no agent assigned) — hide input, show only float button
  const isInQueue = !conversation?.assignedTo;

  // Owner/adm viewing another agent's conversation — can see but not respond
  const userRole = storedSubscription?.profile || 'guest';
  const isOwnerOrAdm = userRole === 'owner' || userRole === 'adm';
  const isConversationMine = conversation?.assignedTo && String(conversation.assignedTo) === String(storedUser?._id);
  const cannotRespond = !isConversationMine && isOwnerOrAdm;

  // True when the conversation has a sourceId but it no longer exists in the loaded sources list.
  // Only evaluated after sources have been fetched at least once to avoid false positives during loading.
  const isChannelRemoved = sourcesLoaded &&
    !!conversation?.sourceId &&
    !sources.some(s => s._id === conversation.sourceId);

  // True when the selected Baileys source exists but its WhatsApp session is disconnected.
  // Distinct from isChannelRemoved: the config still exists, but the phone is not connected.
  const selectedSource = sources.find(s => s._id === selectedSourceId) || null;
  const isChannelDisconnected =
    !isChannelRemoved &&
    sourcesLoaded &&
    !!selectedSource &&
    selectedSource.sourceType === 'baileys' &&
    selectedSource.connectionStatus === 'disconnected';

  // Meta 24h window — disable input when expired (unofficial/Baileys has no restriction).
  // Use selectedSource.sourceType (updated optimistically on source change) before falling
  // back to conversation.sourceType so the footer unlocks immediately when the user
  // switches to a Baileys source, without waiting for the new conversation to load.
  const isMetaWindowClosed = (() => {
    if (selectedSource?.sourceType === 'baileys') return false;
    if (!conversation || conversation.sourceType !== 'meta') return false;
    const msgs = conversation.messages || messages || [];
    let lastInboundTs = null;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].direction === 'inbound') { lastInboundTs = msgs[i].timestamp; break; }
    }
    if (!lastInboundTs) return true;
    return Date.now() - new Date(lastInboundTs).getTime() > 24 * 60 * 60 * 1_000;
  })();

  // Build float actions from config
  const floatActions = omnichannelFloatButton({
    isInQueue,
    isArchived: !!conversation?.archived,
    handlers: {
      onEscalate: () => setShowEscalateModal(true),
      onTransfer: () => setShowTransferModal(true),
      onClose: () => setShowCloseContextModal(true),
      onArchive: () => {
        const newArchived = !conversation?.archived;
        omnichannelService
          .updateConversation(_id, { archived: newArchived })
          .then(() => {
            // Notify ChatMenu immediately (before SSE arrives)
            window.dispatchEvent(new CustomEvent('omnichannel:archive-local', {
              detail: { conversationId: _id, archived: newArchived },
            }));
            appService.message({ flag: 's', text: newArchived ? 'conversation_archived' : 'conversation_unarchived' });
            if (newArchived) { if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); } }
            else setConversation(prev => prev ? { ...prev, archived: false } : prev);
          })
          .catch(() => {
            appService.message({ flag: 'e', text: 'error_operation' });
          });
      },
      onAssume: () => { handleAttend(); },
    },
  });

  // Detect dark theme: parse luminance of colorBgContainer (dark bg = low red channel value)
  const bgHex = token.colorBgContainer.replace('#', '');
  const isDark = parseInt(bgHex.substring(0, 2), 16) < 128;
  const bodyBg = isDark
    ? {
        backgroundImage: `linear-gradient(rgba(11, 20, 26, 0.88), rgba(11, 20, 26, 0.88)), url(${whatsappWallpaperDark})`,
        backgroundColor: '#0b141a',
      }
    : {
        backgroundImage: `linear-gradient(rgba(245, 240, 232, 0.92), rgba(245, 240, 232, 0.92)), url(${whatsappWallpaperLight})`,
        backgroundColor: '#faf7f2',
      };

  // Bubble tail colors match bubble background per theme
  const inboundColor = isDark ? '#1f2c34' : '#ffffff';
  // const outboundColor = isDark ? '#005c4b' : '#dcf8c6';
  const outboundColor = isDark ? '#154D37' : '#dcf8c6';
  const tailShadow = isDark
    ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))'
    : 'drop-shadow(0 1px 2px rgba(11,20,26,0.13))';
  const tailStyles = `
    .bubble-tail-left  { position: relative; z-index: 0; }
    .bubble-tail-left::before {
      content: ''; position: absolute; top: 0; left: -7px; width: 14px; z-index: -1;
      border-top: 9px solid ${inboundColor}; border-left: 7px solid transparent;
    }
    .bubble-tail-right { position: relative; z-index: 0; }
    .bubble-tail-right::before {
      content: ''; position: absolute; top: 0; right: -7px; width: 14px; z-index: -1;
      border-top: 9px solid ${outboundColor}; border-right: 7px solid transparent;
    }
  `;

  // Pre-compute which message indices are the first of their day
  // (used to render the date separator pill once per day group)
  const dayFirstIndices = new Set();
  let lastSeenDay = null;
  messages.forEach((m, idx) => {
    if (!m.timestamp) return;
    const day = new Date(m.timestamp).toDateString();
    if (day !== lastSeenDay) {
      dayFirstIndices.add(idx);
      lastSeenDay = day;
    }
  });

  // Pre-compute context change indices — used to render context flag dividers
  // A context change happens when message[i].contextId differs from message[i-1].contextId
  const contextChangeMap = new Map();
  messages.forEach((m, idx) => {
    if (idx === 0) return;
    const prevContextId = messages[idx - 1]?.contextId;
    const currContextId = m.contextId;
    // Only show divider when BOTH messages have contextIds and they differ
    if (prevContextId && currContextId && String(prevContextId) !== String(currContextId)) {
      // Determine the type of context change based on the previous message's context
      // For now, use 'new_context' as default — will be enriched when contexts array is available
      contextChangeMap.set(idx, 'new_context');
    }
  });

  // Collect unique languages already translated in this conversation
  // (from DB cache in messages[].translations + in-memory translations state from current session)
  const translatedLangs = getTranslatedLangs(messages, translations);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="chat-content" style={{ height: '99.88%', overflow: 'hidden' }}>
      {/* Header */}
      <ConversationHeader
        conversation={conversation}
        displayName={displayName}
        sources={sources}
        selectedSourceId={selectedSourceId}
        sourceSelectOpen={sourceSelectOpen}
        onSourceChange={handleSourceChange}
        onSourceSelect={handleSourceSelect}
        onSourceOpenChange={handleSourceOpenChange}
        translateLang={translateLang}
        onTranslateLangChange={handleTranslateLangChange}
        translating={!!translateProgress?.active}
        translatedLangs={translatedLangs}
        isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen}
        isDark={isDark}
        token={token}
        isInQueue={isInQueue}
        isChannelRemoved={isChannelRemoved}
        isChannelDisconnected={isChannelDisconnected}
        MASKED_PHONE={MASKED_PHONE}
        onRefreshAvatar={handleRefreshAvatar}
        refreshingAvatar={refreshingAvatar}
        embedded={embedded}
      />

      {/* Body + Footer wrapper — position: relative for overlay */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: 0,
        }}
      >
        {/* Unified overlay: loading spinner while fetching, queue buttons when unassigned.
            In embedded mode: auto-assigned (no queue buttons) + no-WhatsApp notice via SSE. */}
        {BLOCK_VIEW_CONVERSATION_NOT_ASSIGN_TO && (
          <ConversationOverlay
            loading={loadingConversation}
            isInQueue={embedded ? false : isInQueue}
            noWhatsapp={embedded && conversation?.contactSnapshot?.hasWhatsapp === false}
            isDark={isDark}
            onAttend={handleAttend}
            attendLoading={attendLoading}
            onDistribute={() => setShowTransferModal(true)}
            isArchived={!!conversation?.archived}
            onArchive={() => {
              const newArchived = !conversation?.archived;
              omnichannelService
                .updateConversation(_id, { archived: newArchived })
                .then(() => {
                  window.dispatchEvent(new CustomEvent('omnichannel:archive-local', {
                    detail: { conversationId: _id, archived: newArchived },
                  }));
                  appService.message({ flag: 's', text: newArchived ? 'conversation_archived' : 'conversation_unarchived' });
                  if (newArchived) { if (onNavigateAway) { onNavigateAway(); } else { navigate(basePath || '/app/operational/omnichannel', { replace: true }); } }
                  else setConversation(prev => prev ? { ...prev, archived: false } : prev);
                })
                .catch(() => {
                  appService.message({ flag: 'e', text: 'error_operation' });
                });
            }}
          />
        )}

        {/* Body */}
        <ConversationBody
          chatBodyRef={chatBodyRef}
          messages={messages}
          conversationId={_id}
          isGroup={conversation?.isGroup}
          groupMembers={conversation?.isGroup ? conversation?.contactSnapshot?.members : null}
          agentAvatar={sources.find(s => s._id === selectedSourceId)?.profilePicture || null}
          dayFirstIndices={dayFirstIndices}
          contextChangeMap={contextChangeMap}
          translations={translations}
          translatingIndex={translatingIndex}
          autoTranslatingIndices={autoTranslatingIndices}
          outboundOriginals={outboundOriginals}
          outboundDeliveryLangs={outboundDeliveryLangs}
          outboundTranslateErrors={outboundTranslateErrors}
          onTranslateMessage={handleTranslateMessage}
          onReaction={handleReaction}
          isChannelRemoved={isChannelRemoved}
          isChannelDisconnected={isChannelDisconnected}
          closedContext={conversation?.status === 'resolved' ? getLastClosedContext(conversation) : null}
          isDark={isDark}
          bodyBg={bodyBg}
          tailStyles={tailStyles}
          tailShadow={tailShadow}
          token={token}
        />

        {/* Footer — hidden when conversation is resolved (no interaction allowed) */}
        {conversation?.status !== 'resolved' && (
          <ConversationFooter
            formRef={formRef}
            inputRef={inputRef}
            sending={sending}
            outboundLang={outboundLang}
            floatOpen={floatOpen}
            floatActions={floatActions}
            isInQueue={isInQueue}
            isMetaWindowClosed={isMetaWindowClosed}
            isChannelRemoved={isChannelRemoved}
            isChannelDisconnected={isChannelDisconnected}
            cannotRespond={cannotRespond}
            BLOCK_VIEW_CONVERSATION_NOT_ASSIGN_TO={BLOCK_VIEW_CONVERSATION_NOT_ASSIGN_TO}
            onSend={handleSend}
            onOutboundLangChange={handleOutboundLangChange}
            onEmptyClick={handleEmptyClick}
            onFloatOpenChange={setFloatOpen}
            onFloatAction={handleFloatAction}
            isDark={isDark}
            bodyBg={bodyBg}
            token={token}
            embedded={embedded}
          />
        )}

        {/* Overlay: translation progress */}
        {translateProgress?.active && (
          <TranslateProgressOverlay
            translateProgress={translateProgress}
            onCancel={handleCancelTranslation}
            isDark={isDark}
            token={token}
          />
        )}
      </div>
      {/* End Body + Footer wrapper */}

      {/* Modals */}
      <TransferModal
        show={showTransferModal}
        setShow={setShowTransferModal}
        conversationId={_id}
        onSuccess={handleTransferSuccess}
      />
      <CloseContextModal
        show={showCloseContextModal}
        setShow={setShowCloseContextModal}
        conversationId={_id}
        onSuccess={handleCloseContextSuccess}
      />
      <EscalateModal
        show={showEscalateModal}
        setShow={setShowEscalateModal}
        conversationId={_id}
        onSuccess={handleEscalateSuccess}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default Conversation;

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Collect unique translation languages from both DB cache (messages[].translations)
// and in-memory session translations state (translations[idx].lang)
function getTranslatedLangs(messages, translations) {
  const langs = new Set();
  // From DB (persisted translations on each message)
  messages.forEach(msg => {
    if (Array.isArray(msg.translations)) {
      msg.translations.forEach(t => {
        if (t.lang) langs.add(t.lang);
      });
    }
  });
  // From current session (in-memory, not yet reloaded from DB)
  Object.values(translations).forEach(t => {
    if (t.lang && !t.outbound) langs.add(t.lang);
  });
  return [...langs];
}

// Format a numeric name (phone number) as masked display — agent never sees the exact number
function formatNumericName(num) {
  if (!num) return '—';
  const digits = num.replace(/\D/g, '');
  // ≤11 digits: BR national number without country code
  if (digits.length <= 11) {
    return parsePhoneToDisplay(num, 'BR', { showCountryCode: true, masked: true });
  }
  // 12+ digits: includes country code
  const withPlus = digits.startsWith('+') ? digits : `+${digits}`;
  return parsePhoneToDisplay(withPlus, undefined, { showCountryCode: true, masked: true });
}

function getLastClosedContext(conv) {
  const contexts = conv?.contexts || [];
  for (let i = contexts.length - 1; i >= 0; i--) {
    if (contexts[i].status === 'closed' || contexts[i].status === 'escalated') {
      return contexts[i];
    }
  }
  return null;
}

function getChannelLabel(channel) {
  if (channel === 'whatsapp') {
    return { color: 'green', icon: <WhatsAppOutlined />, text: 'WhatsApp' };
  }
  return null;
}

// Validate that translation response contains actual translated text rather than an error message.
// Known failure mode: mymemory returns all-caps error strings in the text field when source
// language detection fails (e.g. "'AUTO' IS AN INVALID SOURCE LANGUAGE...").
// Azure also returns detectedSourceLang === 'auto' on SUCCESS, so we cannot reject on that alone.
// Poll the backend for a media URL when SSE message-media-updated may not arrive.
// Checks every 2s for up to 20s. Updates the message in state when URL is found.
function pollForMediaUrl(conversationId, externalMessageId, setMessages, scrollToBottom) {
  let attempts = 0;
  const maxAttempts = 10;
  console.log(`[pollForMediaUrl] Starting poll for ${externalMessageId} in ${conversationId}`);
  const intervalId = setInterval(async () => {
    attempts++;
    console.log(`[pollForMediaUrl] Attempt ${attempts}/${maxAttempts}`);
    try {
      const [data] = await omnichannelService.getConversation(conversationId);
      if (data?.messages) {
        const msg = data.messages.find(m => m.externalMessageId === externalMessageId);
        if (msg?.content?.mediaUrl) {
          clearInterval(intervalId);
          setMessages(prev =>
            prev.map(m =>
              m.externalMessageId === externalMessageId
                ? { ...m, content: { ...m.content, mediaUrl: msg.content.mediaUrl, mimeType: msg.content.mimeType, fileSize: msg.content.fileSize } }
                : m
            )
          );
          setTimeout(scrollToBottom, 150);
        }
      }
    } catch {
      // Ignore — will retry
    }
    if (attempts >= maxAttempts) clearInterval(intervalId);
  }, 2_000);
}

function isValidTranslation(text) {
  if (!text) return false;
  // Detect error messages returned as text (all-caps, long, with provider error keywords)
  const upper = text.toUpperCase();
  if (upper === text && text.length > 50 && /INVALID|ERROR|LANGPAIR/.test(upper)) return false;
  return true;
}
